import { FC, ReactNode, useEffect, useState } from 'react';
import classNames from 'classnames';
import { NavSection } from '../../../ducks/features/navPanelSlice';
import PanelHeader from './PanelHeader';

import './NavPanel.scss';

interface ISectionConfig {
  section: NavSection;
  label: string;
  icon?: string;
  outlined?: boolean;
  customHeader?: boolean;
  children: ReactNode;
  backAction?: { label: string; onBack: () => void };
}

interface INavPanelSectionProps extends ISectionConfig {
  isVisible: boolean;
  onClose: () => void;
}

/**
 * Lazily mounts a section's children the first time it becomes visible, then
 * keeps them mounted. Defers the initial mount so the browser can paint the
 * open panel shell before heavy child trees (Users, Teams, Courses) mount
 * synchronously, and preserves panel-local UI state (expanded rows, search
 * input, open kebab menus) across hover-in / hover-out cycles.
 */
const NavPanelSection: FC<INavPanelSectionProps> = ({
  section,
  isVisible,
  label,
  icon,
  outlined,
  customHeader,
  children,
  backAction,
  onClose,
}) => {
  // Initialise to isVisible so the already-active section on first page
  // load renders immediately without a defer flash.
  const [mountChildren, setMountChildren] = useState(isVisible);

  useEffect(() => {
    if (isVisible) setMountChildren(true);
  }, [isVisible]);

  return (
    <div
      className={classNames('NavPanel-section', {
        'NavPanel-section--visible': isVisible,
      })}
      aria-hidden={!isVisible}
    >
      {customHeader ? null : (
        <PanelHeader
          label={label}
          icon={icon}
          outlined={outlined}
          onClose={onClose}
          backAction={backAction}
        />
      )}
      <div className="NavPanel-section-content">{mountChildren ? children : null}</div>
    </div>
  );
};

interface IProps {
  open: boolean;
  pinned: boolean;
  visibleSection: NavSection | null;
  sections: ISectionConfig[];
  onClose: () => void;
}

const NavPanel: FC<IProps> = ({ open, pinned, visibleSection, sections, onClose }) => {
  return (
    <aside
      id="nav-panel"
      className={classNames('NavPanel', {
        'NavPanel--open': open,
        'NavPanel--pinned': pinned,
      })}
      aria-label="Navigation panel"
    >
      {sections.map((config) => (
        <NavPanelSection
          key={config.section}
          {...config}
          isVisible={visibleSection === config.section}
          onClose={onClose}
        />
      ))}
    </aside>
  );
};

export default NavPanel;
